import { createSystem } from '@iwsdk/core';
import {
  IAssistSessionService,
  IAssistStrokeService,
  IAssistantRosterService,
  IDetectionService,
  ILiveKitRoomService,
  ITrainingStateService,
  hasLiveAudio,
  matchHasWorldPoint,
  stepHasPresentation,
  stepHasWorldLabel,
  stepResult,
  type AssistantEntry,
  type TrainingDetectionMatch,
  type TrainingStep,
  type TrainingStepActivation,
} from '@questvisionstream/client';
import {
  createTrainingUxHost,
  darkCyan,
  buildDefaultPalettes,
  type AssistantCardView,
  type TrainingStepView,
  type TrainingUxHost,
} from '@ethar/uxtraining-web';
import { AppConfig } from '../config';
import { getServiceManager } from '../runtime';
import { poseTracking } from '../rendering/poseTracking';
import {
  classifyJoinCode,
  consoleOriginFromConfigUrl,
  joinCodeFormBody,
  joinCodeFormTitle,
  parseExpiry,
} from '../training/joinCodeModel';

const CANCEL_OPTION = 'Cancel';
const CLOSE_OPTION = 'Close';

/**
 * The presentation glue - the web port of Unity's `TrainingPresentationService`
 * (and the role `TrainingUxGlue` plays for the other host): receives UX
 * requests from the training flow and drives the `@ethar/uxtraining-web`
 * host. Maps each step activation onto the kit's presentation-only
 * `TrainingStepView`, places the world label on the step's detected class via
 * capture-pose unprojection, keeps the hand menu's readout and audio tiles
 * fresh, paints the assistant grid from the roster + room video tracks, and
 * feeds option presses back as `TrainingStepResult`s.
 */
export class TrainingUxSystem extends createSystem({}) {
  private host: TrainingUxHost | null = null;
  private readonly unsubs: Array<() => void> = [];
  private readonly remoteVideo = new Map<string, MediaStreamTrack>();
  private pickerVisible = false;
  private joinCodeVisible = false;
  private worldLabelEnabled = true;

  override init(): void {
    const manager = getServiceManager();
    const room = manager.resolve(ILiveKitRoomService);
    const training = manager.resolve(ITrainingStateService);
    const detections = manager.resolve(IDetectionService);
    const assist = manager.resolve(IAssistSessionService);
    const roster = manager.resolve(IAssistantRosterService);
    const strokes = manager.resolve(IAssistStrokeService);

    const palettes = buildDefaultPalettes();
    const palette = palettes[AppConfig.ux.themeIndex] ?? darkCyan();

    this.host = createTrainingUxHost({
      world: this.world,
      palette,
      formDistanceMeters: AppConfig.ux.formDistanceMeters,
      onOptionPressed: (index) => this.triggerOption(index),
      brand: 'ETHAR TRAINING',
      menuActions: {
        home: () => this.restartScenario(),
        tasks: () => this.showCurrentStep(),
        redo: () => this.restartScenario(),
        exit: () => this.exitScenario(),
        // Only offered when there is somewhere to switch TO.
        swap: training.scenarioCatalog.length > 1 ? () => this.showScenarioPicker() : undefined,
        // On the hand menu, not a controller button: a wearer using hand
        // tracking has no button to press, and needing help is exactly the
        // moment to not also need a controller.
        help: () => this.showJoinCode(),
        // Two separate toggles, deliberately: "stop them hearing me" and
        // "stop me hearing them" are different needs.
        micMute: () => assist.toggleMicrophoneMuted(),
        assistantMute: () => roster.toggleAllMuted(),
        // Session-wide: broadcasts the wearer-only clear/all on the strokes
        // topic, so consoles wipe their overlays too.
        clearDrawings: () => strokes.publishClearAll(),
      },
      settings: { windowPlacement: AppConfig.ux.windowPlacement },
    });
    this.host.setHandMenuStep('TRAINING', training.scenario ? training.scenario.name : 'Waiting…');

    this.unsubs.push(
      training.on('scenarioLoaded', (scenario) => this.host?.setHandMenuStep('TRAINING', scenario.name)),
      training.on('stepActivated', (activation) => this.onStepActivated(activation)),
      training.on('currentClassSighted', (match) => this.onCurrentClassSighted(match)),
      training.on('scenarioCompleted', () => this.onScenarioCompleted()),
      // Every input the two audio tiles describe: consent moves the mic
      // without the wearer touching it, and a join/leave decides whether
      // there is anything to hear.
      assist.on('tilesChanged', () => this.refreshAudioTiles()),
      roster.on('assistantJoined', () => this.pushAssistantCards()),
      roster.on('assistantLeft', () => this.pushAssistantCards()),
      roster.on('assistantChanged', () => this.pushAssistantCards()),
      room.on('remoteVideoTrack', ({ identity, track }) => {
        if (track) this.remoteVideo.set(identity, track);
        else this.remoteVideo.delete(identity);
        this.pushAssistantCards();
      }),
      // Auto-begin the moment the user enters: the session is negotiated
      // during warm-up, so Enter (streamingBegan) is the normal trigger; the
      // state change and the server's ready handshake stay wired for the
      // rare orders where Enter lands before Connected.
      room.on('streamingBegan', () => this.autoBegin()),
      room.on('stateChange', (state) => {
        if (state === 'connected') this.autoBegin();
      }),
      room.on('ready', () => this.autoBegin()),
    );

    this.refreshAudioTiles();
    this.pushAssistantCards();
    this.autoBegin();
    void detections;
  }

  override update(delta: number): void {
    // The flat warm-up page owns the screen until a session starts; the
    // spatial UX shows only inside one (real headset or the dev emulator).
    this.host?.setVisible(this.world.session !== undefined && this.world.session !== null);
    this.host?.update(delta);
  }

  override destroy(): void {
    this.unsubs.forEach((unsub) => unsub());
    this.unsubs.length = 0;
    this.host?.dispose();
    this.host = null;
  }

  // ---------------------------------------------------------------- helpers

  private get training() {
    return getServiceManager().resolve(ITrainingStateService);
  }

  private get room() {
    return getServiceManager().resolve(ILiveKitRoomService);
  }

  private autoBegin(): void {
    const room = this.room;
    const training = this.training;
    // Both gates: the user pressed Enter (frames enabled) AND the session is
    // connected - the welcome form appears immediately, never behind the
    // warm-up card.
    if (AppConfig.ux.autoBegin && training.status === 'idle' && room.streamingRequested && room.isRoomConnected) {
      training.begin();
    }
  }

  private showCurrentStep(): void {
    if (this.training.currentStep) this.host?.representForm();
  }

  private triggerOption(optionIndex: number): void {
    const training = this.training;
    // The picker borrows the step form, so its presses must not be read as
    // step results - it owns the option indices while it is up.
    if (this.pickerVisible) {
      this.selectScenario(optionIndex);
      return;
    }
    // The HELP form borrows the same surface. Its only option puts the
    // interrupted step back.
    if (this.joinCodeVisible) {
      this.joinCodeVisible = false;
      this.representCurrentStep();
      return;
    }

    const step = training.currentStep;
    if (!step || !this.host?.isFormVisible) return;
    const optionLabel = step.options[optionIndex] ?? '';
    // The custom response, fed back to the flow: the result pair travels the
    // training topic; an empty result class reuses the step's waiting class.
    training.completeStep(stepResult(training.currentStepIndex, step, optionLabel));
  }

  private onStepActivated(activation: TrainingStepActivation): void {
    const host = this.host;
    if (!host) return;
    host.showStep(this.toView(activation.step, activation.stepIndex, activation.stepCount));
    host.setHandMenuStep(
      `STEP ${activation.stepIndex + 1}/${activation.stepCount}`,
      stepHasPresentation(activation.step) ? activation.step.title : '…',
    );

    // Location indicator: label the class that brought us here, at the
    // detected box centre unprojected through the capture-time pose.
    const point = activation.match ? this.worldPointOf(activation.match) : null;
    if (this.worldLabelEnabled && stepHasWorldLabel(activation.step) && point) {
      host.showWorldLabel(point, activation.step.label);
    } else {
      host.hideWorldLabel();
    }
  }

  private onCurrentClassSighted(match: TrainingDetectionMatch): void {
    const step = this.training.currentStep;
    const point = this.worldPointOf(match);
    if (this.worldLabelEnabled && step && stepHasWorldLabel(step) && point) {
      this.host?.showWorldLabel(point, step.label);
    }
  }

  private onScenarioCompleted(): void {
    this.host?.hideForm();
    this.host?.hideWorldLabel();
    this.host?.setHandMenuStep('TRAINING', 'Complete - HOME restarts');
  }

  private restartScenario(): void {
    this.pickerVisible = false;
    this.joinCodeVisible = false;
    this.host?.hideForm();
    this.host?.hideWorldLabel();
    this.training.resetScenario();
    this.training.begin();
  }

  private exitScenario(): void {
    this.pickerVisible = false;
    this.joinCodeVisible = false;
    this.host?.hideForm();
    this.host?.hideWorldLabel();
    this.training.resetScenario();
    this.host?.setHandMenuStep('TRAINING', 'Exited - HOME restarts');
  }

  /**
   * Hand menu HELP: the join code and where to type it (D27). Read live from
   * the last served config on every press, never cached: a code can be
   * rotated or revoked mid-session, and a stale code wastes another person's
   * time as well as the wearer's.
   */
  private showJoinCode(): void {
    const room = this.room;
    const config = room.lastRemoteConfig;
    const code = config?.assistCode ?? '';
    const state = classifyJoinCode(code, parseExpiry(config?.assistCodeExpiresAt), Date.now(), config !== null);
    this.joinCodeVisible = true;
    this.host?.hideWorldLabel();
    this.host?.showStep({
      stepIndex: 0,
      stepCount: 0,
      title: joinCodeFormTitle(state),
      description: joinCodeFormBody(state, code, consoleOriginFromConfigUrl(room.configEndpoint)),
      options: [CLOSE_OPTION],
    });
    this.host?.setHandMenuStep('TRAINING', 'Help…');
  }

  /** Hand menu SWAP: list the configured scenarios in the step form. */
  private showScenarioPicker(): void {
    const training = this.training;
    const catalog = training.scenarioCatalog;
    if (catalog.length <= 1) return;
    const options = catalog.map((name, index) => (index === training.activeScenarioIndex ? `${name} (current)` : name));
    options.push(CANCEL_OPTION);
    this.pickerVisible = true;
    this.host?.hideWorldLabel();
    this.host?.showStep({
      stepIndex: 0,
      stepCount: 0,
      title: 'Select Training',
      description: 'Choose a training configuration. Switching restarts the exercise from its first step.',
      options,
    });
    this.host?.setHandMenuStep('TRAINING', 'Select…');
  }

  private selectScenario(optionIndex: number): void {
    const training = this.training;
    this.pickerVisible = false;
    // Anything past the catalog is the trailing Cancel.
    if (optionIndex < 0 || optionIndex >= training.scenarioCatalog.length) {
      this.representCurrentStep();
      return;
    }
    if (optionIndex === training.activeScenarioIndex) {
      this.restartScenario();
      return;
    }
    if (!training.switchScenario(optionIndex)) {
      console.warn(`[WebXRClient] switch to '${training.scenarioCatalog[optionIndex]}' failed - keeping the current configuration`);
      this.representCurrentStep();
      return;
    }
    this.restartScenario();
  }

  /** Re-show the active step's form after the picker or HELP form is dismissed. */
  private representCurrentStep(): void {
    const training = this.training;
    const step = training.currentStep;
    const scenario = training.scenario;
    if (!step || !scenario) {
      this.host?.hideForm();
      this.host?.setHandMenuStep('TRAINING', scenario ? scenario.name : 'Waiting…');
      return;
    }
    this.host?.showStep(this.toView(step, training.currentStepIndex, scenario.steps.length));
    this.host?.setHandMenuStep(
      `STEP ${training.currentStepIndex + 1}/${scenario.steps.length}`,
      stepHasPresentation(step) ? step.title : '…',
    );
  }

  private toView(step: TrainingStep, stepIndex: number, stepCount: number): TrainingStepView {
    return {
      stepIndex,
      stepCount,
      title: step.title,
      description: step.description,
      options: step.options,
      imageRef: step.imageRef,
      imageUrl: this.resolveStepImage(step.imageRef),
    };
  }

  /**
   * Step images resolve against the running scenario's own name - the one
   * identity both authority modes have: `/training/<scenario name, spaces
   * removed>/<imageRef>.png` under the site root. Unresolvable references
   * yield no image area.
   */
  private resolveStepImage(imageRef: string): string | undefined {
    const scenario = this.training.scenario;
    if (!imageRef || !scenario) return undefined;
    return `${AppConfig.ux.imageRoot}/${scenario.name.replace(/\s+/g, '')}/${imageRef}.png`;
  }

  private worldPointOf(match: TrainingDetectionMatch): { x: number; y: number; z: number } | null {
    if (!matchHasWorldPoint(match)) return null;
    const point = poseTracking.unprojectAtArrival(match.center, match.arrivalTimeMs, AppConfig.ux.labelPlacementDistanceMeters);
    return point ? { x: point.x, y: point.y, z: point.z } : null;
  }

  private refreshAudioTiles(): void {
    const manager = getServiceManager();
    const assist = manager.resolve(IAssistSessionService);
    const mic = assist.micTile;
    this.host?.setHandMenuMic(mic.glyph, mic.label, mic.available, mic.on);
    const hear = assist.assistantAudioTile;
    this.host?.setHandMenuAssistantAudio(hear.glyph, hear.label, hear.available, hear.on);
  }

  /** Hand the current assistant cards to the UX - one per connected assistant. */
  private pushAssistantCards(): void {
    const roster = getServiceManager().resolve(IAssistantRosterService);
    const cards: AssistantCardView[] = roster.assistants.map((entry: AssistantEntry) => ({
      id: entry.identity,
      name: entry.displayName,
      video: entry.hiddenByWearer ? null : (this.remoteVideo.get(entry.identity) ?? null),
      muted: entry.mutedByWearer || !hasLiveAudio(entry),
    }));
    this.host?.setAssistants(cards);
  }
}
